import { addField, addGroup } from 'live/widgets/helpers'
import DataService from 'live/services/charts/data'
import SpanService from 'live/services/span'
import { LollipopEvent, LollipopState } from './types'
import { mergeEvents } from 'plugin-live-exploratory-viz/src/utils'
import { reducerFlags } from '../services/reducerFlags'

const supportedMetas = ['__color', '__markerSymbol', '__markerRadius']

export function startQuery(actions: StartFlowMessage, state: LollipopState): LollipopState {
    if (!actions?.events) {
        return state
    }

    const { select, group } = actions.events

    const newState = {
        span: { begin: 0, end: Number.MAX_SAFE_INTEGER },
        realtime: SpanService.isRealtime(actions.events.info?.span?.text),
        fields: [],
        groups: [],
        metas: {}
    }

    select?.forEach(function (field) {
        addField(newState.fields, field)
    })
    group?.forEach(function (field) {
        addGroup(newState.groups, field)
    })

    for (const key in actions.events.info) {
        const founded = supportedMetas.find(meta => meta === key)
        if (founded) {
            newState.metas = {
                ...newState.metas,
                [founded]: actions.events.info[key]
            }
        }
    }

    return {
        ...state,
        ...newState
    }
}

export function getInitialState(): LollipopState {
    return {
        historyEnded: false,
        series: [],
        bufferEvents: [],
        fields: [],
        groups: [],
        metas: {}
    }
}

export function accEvents(action: EventFlowMessage<LiveEvent<LollipopEvent>>, state: LollipopState): LollipopState {
    let allEvents = []
    state.fields.forEach(field => {
        action.events.forEach(event => {
            const value = event[field]
            if (typeof value !== 'number') return

            let name = DataService.makeGroupValue(state.groups, event, ', ')
            if (!name) name = field

            const date = event.timestamp

            let metas = { ...state.metas }
            for (const key in event) {
                const founded = supportedMetas.find(meta => meta === key)
                if (founded) {
                    metas = {
                        ...metas,
                        [founded]: event[key]
                    }
                }
            }

            const eventPoint = {
                name,
                x: date,
                y: value,
                radius: metas.__markerRadius || null,
                symbol: metas.__markerSymbol || null,
                color: metas.__color || null
            }

            allEvents.push(eventPoint)
        })
    })

    const bufferEvents = [...state.bufferEvents]
    allEvents.forEach(e => bufferEvents.push(e))

    const series = mergeEvents(state.series, allEvents)

    allEvents = []
    const newState = { ...state, bufferEvents, series }

    return newState
}

export function getEndState(state: LollipopState): LollipopState {
    const groups = mergeEvents([], state.bufferEvents)

    const { begin, end } = state.span

    const series = groups.flatMap(item => {
        const filteredData = item.points.filter(point => point.x >= begin && point.x <= end)

        return { ...item, points: filteredData }
    })

    return {
        ...state,
        historyEnded: true,
        series: series,
        bufferEvents: []
    }
}

export function reflectSpan(action: SpanFlowMessage, state: LollipopState): LollipopState {
    const begin = action.events.begin
    const end = action.events.end
    const span = { begin, end }

    if (!reducerFlags.followSpan) {
        return { ...state, span }
    }

    const series = state.series.map(element => {
        const filtered = element.points.filter(point => point.x >= begin && point.x <= end)

        if (element.points.length === filtered.length) {
            return element
        } else {
            return { ...element, points: filtered }
        }
    })

    const newState = { ...state, span, series }

    return newState
}
